import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import AutomationService from "../services/AutomationService";
import appConfig from "../shared/js/app-config";

/**
 * Default / catalog chatbots: preview in canvas (read-only) or clone to tenant.
 */
export default function ChatbotLibraryPanel({ onAfterClone, previewFrom = "/automation/chatbots?view=library" }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloningId, setCloningId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [myBots, setMyBots] = useState([]);

  const serviceId = appConfig.getServiceId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defaults, mine] = await Promise.all([
        AutomationService.getDefaultChatbots(),
        AutomationService.getChatBot(),
      ]);
      setRows(defaults);
      setMyBots(mine);
    } catch (e) {
      setRows([]);
      setMyBots([]);
      setSnackbar({ open: true, message: "Failed to load library chatbots", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isAlreadyCloned = (defaultId) =>
    (myBots || []).some(
      (b) => b.getSourceDefaultChatbotId && b.getSourceDefaultChatbotId() === defaultId
    );

  const openPreview = (row) => {
    navigate(`/flow/preview-default/${row.id}`, {
      state: {
        templateJson: row.template,
        from: previewFrom,
      },
    });
  };

  const handleClone = async (row) => {
    if (isAlreadyCloned(row.id)) {
      return;
    }
    setCloningId(row.id);
    try {
      const created = await AutomationService.cloneDefaultChatbot(row.id, serviceId);
      const newId = created?.id;
      setSnackbar({
        open: true,
        message: "Chatbot added to your account. You can edit it under Your bots.",
        severity: "success",
      });
      await load();
      onAfterClone?.();
      if (newId) {
        navigate(`/flow/${newId}`);
      } else {
        navigate("/automation/chatbots");
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || e?.message || "Failed to add chatbot",
        severity: "error",
      });
    } finally {
      setCloningId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 2,
          p: 5,
          textAlign: "center",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          No templates in the library yet
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          When published templates are available, they will appear here.
        </Typography>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: "'Poppins', sans-serif" }}>
        Browse ready-made flows. Open in canvas to preview, then add to your account to customize.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 700 }}>Chatbot</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Trigger</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const already = isAlreadyCloned(row.id);
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{row.templateName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {row.description || "Ready-to-use flow template for WhatsApp automation."}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.trigger || "—"}</TableCell>
                  <TableCell>{row.action || "—"}</TableCell>
                  <TableCell>
                    {already ? (
                      <Chip size="small" color="success" label="Added" />
                    ) : (
                      <Chip size="small" variant="outlined" label="Not added" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="outlined" onClick={() => openPreview(row)}>
                        Open in canvas
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={already || cloningId === row.id}
                        onClick={() => handleClone(row)}
                      >
                        {already
                          ? "Already in your chatbots"
                          : cloningId === row.id
                            ? "Adding..."
                            : "Use this chatbot"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
